import {useEffect, useMemo, useState} from "react";
import {Loader} from "rsuite";
import {useNavigate} from "react-router-dom";
import {UserActionDto} from "../../../api";
import JournalApi from "../../../services/Journal.api";
import {getApiErrorMessage} from "../../../services/ApiErrorMessage";
import {formatDayTitle, formatTime, groupByDay} from "./journal.utils";

type ActionsLogProps = {
  deviceId: string;
  from: Date;
  to: Date;
};

const ALL = "";

// «Было»: значение настройки до изменения (из предыдущей записи журнала)
const previousText = (action: UserActionDto): string | null => {
  if (!action.value) return null;
  if (!action.previousValue) return "было: нет данных — раньше не менялось и со станции не прочитано";
  if (action.previousValue === action.value) return "было: то же значение";
  return `было: ${action.previousValue}`;
};

const whoDetails = (action: UserActionDto): string =>
  [action.isAdmin ? "администратор" : "клиент", action.client && action.client !== "—" ? action.client : null]
    .filter(Boolean)
    .join(" · ");

// Раздел «Действия пользователей»: что нажимали в приложении, кто и чем закончилось
// Подпись ссылки на устройство по тексту действия («Механизм №5008: …», «Диммер №5: …»)
const deviceLinkText = (description?: string | null) => {
  if (/^Механизм/i.test(description ?? "")) return "открыть механизм →";
  if (/^Диммер/i.test(description ?? "")) return "открыть диммер →";
  return "открыть устройство →";
};

const ActionsLog = ({deviceId, from, to}: ActionsLogProps) => {
  const navigate = useNavigate();
  const [actions, setActions] = useState<UserActionDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [user, setUser] = useState(ALL);
  const [section, setSection] = useState(ALL);
  const [search, setSearch] = useState("");
  const [onlyTime, setOnlyTime] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    JournalApi.getUserActions(deviceId, from, to)
      .then((result) => {
        if (!cancelled) setActions(result ?? []);
      })
      .catch((err) => {
        if (!cancelled) {
          setActions([]);
          setError(getApiErrorMessage(err, "Не удалось загрузить журнал действий"));
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [deviceId, from, to]);

  const users = useMemo(() => Array.from(new Set(actions.map((x) => x.userLogin ?? "").filter(Boolean))).sort(), [actions]);
  const sections = useMemo(() => Array.from(new Set(actions.map((x) => x.section ?? "").filter(Boolean))).sort(), [actions]);

  const filtered = useMemo(() => {
    const text = search.trim().toLowerCase();
    return actions.filter((x) =>
      (user === ALL || x.userLogin === user)
      && (section === ALL || x.section === section)
      && (!onlyTime || x.affectsTime)
      && (!text || `${x.description ?? ""} ${x.previousValue ?? ""} ${x.error ?? ""} ${x.userLogin ?? ""}`.toLowerCase().includes(text)));
  }, [actions, user, section, search, onlyTime]);

  const groups = useMemo(() => groupByDay(filtered, (x) => x.createdAt), [filtered]);

  return (
    <div className="journal-actions">
      <div className="journal-filters">
        <select className="journal-filters__select" value={user} onChange={(e) => setUser(e.target.value)} aria-label="Пользователь">
          <option value={ALL}>Все пользователи</option>
          {users.map((x) => <option key={x} value={x}>{x}</option>)}
        </select>
        <select className="journal-filters__select" value={section} onChange={(e) => setSection(e.target.value)} aria-label="Раздел">
          <option value={ALL}>Все разделы</option>
          {sections.map((x) => <option key={x} value={x}>{x}</option>)}
        </select>
        <input
          className="journal-filters__search"
          type="search"
          placeholder="Поиск по действиям"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <label className="journal-filters__check">
          <input type="checkbox" checked={onlyTime} onChange={(e) => setOnlyTime(e.target.checked)}/>
          Только влияющие на время
        </label>
      </div>

      {loading && <div className="journal__state"><Loader content="Загрузка…"/></div>}
      {!loading && error && <div className="journal__state journal__state--error">{error}</div>}
      {!loading && !error && filtered.length === 0 && (
        <div className="journal__state">За выбранный период действий нет</div>
      )}

      {!loading && !error && filtered.length > 0 && (
        <div className="journal-table">
          <div className="journal-table__head">
            <span>Время</span>
            <span>Кто</span>
            <span>Раздел</span>
            <span>Действие</span>
            <span>Результат</span>
          </div>
          {groups.map((group) => (
            <div key={group.key} className="journal-table__group">
              <div className="journal-table__day">{formatDayTitle(group.key)}</div>
              {group.items.map((action) => (
                <div
                  key={action.id ?? `${action.createdAt}-${action.description}`}
                  className={`journal-table__row${action.affectsTime ? " journal-table__row--time" : ""}`}
                >
                  <span className="journal-table__time">{formatTime(action.createdAt, true)}</span>
                  <span className="journal-table__who">
                    <b>{action.userLogin || "неизвестно"}</b>
                    <small>{whoDetails(action)}</small>
                  </span>
                  <span className="journal-table__section">{action.section}</span>
                  <span className="journal-table__action">
                    {action.description}
                    {action.affectsTime && <em className="journal-badge journal-badge--time">влияет на время</em>}
                    {previousText(action) && <small className="journal-table__was">{previousText(action)}</small>}
                    {action.deviceId && (
                      <button type="button" className="journal-event__link"
                              onClick={() => navigate(`/devices/${deviceId}?tab=devices&externalId=${action.deviceId}`)}>
                        {deviceLinkText(action.description)}
                      </button>
                    )}
                  </span>
                  <span className={`journal-table__result${action.success ? " journal-table__result--ok" : " journal-table__result--error"}`}>
                    {action.success ? "Выполнено" : `Ошибка: ${action.error ?? "причина неизвестна"}`}
                  </span>
                </div>
              ))}
            </div>
          ))}
          {actions.length >= 5000 && (
            <div className="journal__hint">Показаны последние 5000 действий — выберите период короче.</div>
          )}
        </div>
      )}
    </div>
  );
};

export default ActionsLog;
